// Tab "Đơn" của trang Đối soát: từng đơn có dòng tiền của tôi, mới nhất trước, lọc theo nhãn
// trạng thái tiền. Cột "Số tiền của tôi" tách theo loại (thực nhận, chênh lệch, hoa hồng…).

import { useNavigate, useSearchParams } from 'react-router-dom'
import { useInfiniteQuery } from '@tanstack/react-query'
import { get } from '@/lib/api'
import { formatDate, money } from '@/lib/format'
import { Button } from '@/components/ui/button'
import { FilterTabs } from '@/components/ui/filter-tabs'
import { LoadError } from '@/components/ui/data-notice'
import DataTable, { type DataTableColumn } from '@/components/ui/data-table'
import { MoneyStageBadge, type OrderMoneyView } from '@/components/orders/order-money'
import { AmountByKind } from './parts'
import { STAGE_FILTERS, outcomeLabel, type ReconcileOrderRow, type ReconcileOrdersPage, type UnitRole } from './types'

const PAGE = 50

export function ReconcileOrders({ selfId, role }: { selfId: string | null; role: UnitRole }) {
  const navigate = useNavigate()
  // Bộ lọc nằm trên URL (?stage=ready) để các thẻ tổng phía trên dẫn thẳng tới đúng nhóm đơn.
  const [params, setParams] = useSearchParams()
  const requested = params.get('stage')
  const stage = STAGE_FILTERS.some((f) => f.value === requested) ? requested! : 'all'
  const setStage = (v: string) => setParams(v === 'all' ? {} : { stage: v }, { replace: true })

  const q = useInfiniteQuery({
    queryKey: ['reconcile', 'orders', stage],
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) => {
      const p = new URLSearchParams({ limit: String(PAGE) })
      if (stage !== 'all') p.set('stage', stage)
      if (pageParam) p.set('before', pageParam)
      return get<ReconcileOrdersPage>(`/reconcile/orders?${p.toString()}`)
    },
    getNextPageParam: (last) => last.nextBefore ?? null,
  })

  const rows = q.data?.pages.flatMap((p) => p.orders) ?? []

  const openOrder = (r: ReconcileOrderRow) => {
    if (!r.orderId) return
    navigate(r.shopId && r.shopId !== selfId ? `/orders/${encodeURIComponent(r.orderId)}?shopId=${encodeURIComponent(r.shopId)}` : `/orders/${encodeURIComponent(r.orderId)}`)
  }

  const columns: DataTableColumn<ReconcileOrderRow>[] = [
    {
      key: 'order',
      header: 'Mã vận đơn',
      priority: 'primary',
      cell: (r) => (
        <span className="block min-w-0">
          <span className="block font-mono text-sm font-medium text-ink">{r.waybillCode ?? 'Chưa có vận đơn'}</span>
          <span className="block truncate text-xs text-muted">
            {r.orderId ?? '—'}
            {role !== 'shop' && (r.shopId === selfId ? ' · Đơn tự bán' : ` · ${r.shopName ?? 'Đơn của cấp dưới'}`)}
          </span>
        </span>
      ),
    },
    {
      key: 'outcome',
      header: 'Kết quả đơn',
      priority: 'secondary',
      cell: (r) => (
        <span className="block">
          <span className="block text-inkSoft">{outcomeLabel(r.status, r.returnStage)}</span>
          {r.deliveredAt && <span className="block text-xs text-muted">Giao {formatDate(r.deliveredAt)}</span>}
        </span>
      ),
    },
    {
      key: 'cod',
      header: 'COD thu',
      align: 'right',
      cell: (r) => <span className="whitespace-nowrap font-mono tabular-nums text-ink">{money(r.cod)}</span>,
    },
    {
      key: 'amount',
      header: 'Số tiền của tôi',
      align: 'right',
      cell: (r) => (r.stage === 'void' ? <span className="font-mono text-muted">—</span> : <AmountByKind amount={r.amount} byKind={r.byKind} />),
    },
    {
      key: 'stage',
      header: 'Trạng thái tiền',
      // Tạm giữ chờ thu nợ: người nợ là đơn vị bán của đơn (cấp dưới của tôi).
      cell: (r) => (
        <MoneyStageBadge
          money={{ stage: r.stage, voucherCode: r.voucherCode, holdReason: r.holdReason as OrderMoneyView['holdReason'] }}
          debtorName={r.shopId !== selfId ? r.shopName : null}
        />
      ),
    },
  ]

  return (
    <section className="space-y-4">
      <FilterTabs value={stage} onChange={setStage} options={STAGE_FILTERS} />

      <div className="overflow-hidden rounded-xl bg-raise shadow-card">
        {q.isError ? (
          <LoadError onRetry={() => q.refetch()}>Không tải được danh sách đơn.</LoadError>
        ) : (
          <DataTable
            columns={columns}
            data={rows}
            rowKey={(r) => r.financeId}
            loading={q.isLoading}
            onRowClick={openOrder}
            emptyLabel={stage === 'all' ? 'Chưa có đơn' : 'Không có đơn'}
          />
        )}
      </div>

      {(q.hasNextPage || rows.length > 0) && (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-xs text-muted">
            Đang hiện {rows.length.toLocaleString('vi-VN')} đơn.
          </p>
          {q.hasNextPage && (
            <Button variant="outline" onClick={() => q.fetchNextPage()} disabled={q.isFetchingNextPage}>
              {q.isFetchingNextPage ? 'Đang tải…' : 'Xem thêm'}
            </Button>
          )}
        </div>
      )}
    </section>
  )
}
